import { useEffect, useState, useCallback, useMemo } from 'react';
import Globals from '../config/globals';
import { userAssociatedFacilities, selectedDashboardFacility } from '../common/services/ApiService';

const STORAGE_KEY = 'dashboardFacility';
const CLEARED_SENTINEL = '__cleared__';

function readStoredSelection() {
    try {
        const stored = window.sessionStorage.getItem(STORAGE_KEY);
        if (!stored) return null;
        if (stored === CLEARED_SENTINEL) return { facility: null, hasSelection: true };
        return { facility: JSON.parse(stored), hasSelection: true };
    } catch {
        return null;
    }
}

function persistSelection(facility) {
    window.sessionStorage.setItem(STORAGE_KEY, facility ? JSON.stringify(facility) : CLEARED_SENTINEL);
    Globals.dashboardFacility = facility;
    Globals.dashboardFacilityHasSelection = true;
    selectedDashboardFacility.next({ facility, hasSelection: true });
}

function clearSelectionEntirely() {
    window.sessionStorage.removeItem(STORAGE_KEY);
    Globals.dashboardFacility = null;
    Globals.dashboardFacilityHasSelection = false;
    selectedDashboardFacility.next({ facility: null, hasSelection: false });
}

/**
 * Global dashboard facility selection, shared across all dashboard widgets.
 * Session-scoped (resets on logout/idle) rather than a persistent cross-login "home facility".
 * Mirrors the auto-select-if-one-facility pattern used elsewhere in the app.
 *
 * A facility can be: unset (auto-default applies), explicitly chosen, or explicitly
 * cleared back to "All Facilities" (hasSelection stays true so auto-default won't
 * silently re-select a default facility on top of the user's explicit choice).
 */
export function useSelectedDashboardFacility() {
    const [facilities, setFacilities] = useState([]);
    const [selection, setSelection] = useState(() => {
        const stored = readStoredSelection();
        if (stored) return stored;
        if (Globals.dashboardFacilityHasSelection) {
            return { facility: Globals.dashboardFacility, hasSelection: true };
        }
        return { facility: null, hasSelection: false };
    });

    useEffect(() => {
        const subscription = userAssociatedFacilities.subscribe(list => {
            if (list?.length > 0) setFacilities(list);
        });
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        const subscription = selectedDashboardFacility.subscribe(s => setSelection(s || { facility: null, hasSelection: false }));
        return () => subscription.unsubscribe();
    }, []);

    // Auto-default once the facility list is known, but only if the user hasn't made
    // an explicit choice yet (including explicitly clearing back to All Facilities).
    useEffect(() => {
        if (facilities.length === 0 || selection.hasSelection) return;

        if (facilities.length === 1) {
            persistSelection(facilities[0].facilityModel);
        } else if (Globals.defaultFacility?.facilityID) {
            persistSelection(Globals.defaultFacility);
        }
    }, [facilities, selection.hasSelection]);

    // Clear a stale explicit selection (e.g. facility access was revoked) once the real list is known.
    useEffect(() => {
        if (facilities.length === 0 || !selection.hasSelection || !selection.facility) return;
        const stillValid = facilities.some(f => f.facilityModel.facilityID === selection.facility.facilityID);
        if (!stillValid) clearSelectionEntirely();
    }, [facilities, selection]);

    const setFacility = useCallback((facilityModel) => {
        persistSelection(facilityModel || null);
    }, []);

    // Explicitly choose "All Facilities" - distinct from never having chosen at all,
    // so the auto-default logic doesn't immediately re-select a default facility.
    const clearFacility = useCallback(() => {
        persistSelection(null);
    }, []);

    const facilityId = selection.facility?.facilityID || null;
    const selectedFacilities = useMemo(
        () => facilityId ? [facilityId] : [],
        [facilityId]
    );

    return {
        facility: selection.facility,
        facilityId,
        selectedFacilities,
        facilities,
        setFacility,
        clearFacility
    };
}

export default useSelectedDashboardFacility;
